import { endOfDay } from "date-fns";
import { formatDisplayDate } from "../../../../common/utils/date";

type PeriodDate = string | Date | null | undefined;

const toTime = (value: PeriodDate): number | null => {
  if (!value) return null;
  const time = (value instanceof Date ? value : new Date(value)).getTime();
  return isNaN(time) ? null : time;
};

/**
 * A prescribed service's period, "start - end", cut short by its
 * `deactivationDate`: a service deactivated before its end date ran until the
 * deactivation, and one deactivated before it started never ran at all — the
 * period is replaced by the deactivation date.
 *
 * Shared by the prescription detail's service card (ISO timestamps) and the
 * History service detail (date-only `dd-MM-yyyy` period, parsed by the caller
 * to local midnight, so there the comparison is by day).
 */
export const formatPrescriptionPeriod = ({
  startDate,
  endDate,
  deactivationDate,
}: {
  startDate: PeriodDate;
  endDate?: PeriodDate;
  deactivationDate?: PeriodDate;
}): string => {
  const start = toTime(startDate);
  const deactivation = toTime(deactivationDate);

  if (deactivation !== null && start !== null && deactivation < start) {
    return `Deactivated on ${formatDisplayDate(deactivationDate)}`;
  }

  const end = toTime(endDate);
  const effectiveEnd =
    deactivation !== null && (end === null || deactivation < end)
      ? deactivationDate
      : endDate;

  const formattedStartDate = formatDisplayDate(startDate);
  const formattedEndDate = formatDisplayDate(effectiveEnd);
  return !formattedEndDate || formattedStartDate === formattedEndDate
    ? formattedStartDate
    : `${formattedStartDate} - ${formattedEndDate}`;
};

export type PrescribedServiceStatus = "active" | "expired" | "inactive";

/**
 * When the service stopped (or will stop) running: its end date, or the
 * deactivation date when that came first.
 */
export const effectiveEndDate = ({
  endDate,
  deactivationDate,
}: {
  endDate?: string | null;
  deactivationDate?: string | null;
}): string | null => {
  const end = toTime(endDate);
  const deactivation = toTime(deactivationDate);
  if (deactivation !== null && (end === null || deactivation < end)) {
    return deactivationDate ?? null;
  }
  return endDate ?? null;
};

/**
 * A prescribed service's status, worked out from its dates. The backend's
 * `expired`/`isExpired` stay 0 after the end date passes, so they can't be
 * used, and `active` alone can't tell a service cut short from one that ran
 * its full period.
 *
 * - inactive: deactivated before its end date, or switched off with no date.
 * - expired: the end date's day is over, with no deactivation before it.
 *   The end date counts as a whole day, so a one-day service (start = end)
 *   stays active through that day rather than expiring at its end time.
 * - active: `active` is 1 and the end date is still ahead.
 */
export const prescribedServiceStatus = (
  {
    active,
    endDate,
    deactivationDate,
  }: {
    active?: number | null;
    endDate?: string | null;
    deactivationDate?: string | null;
  },
  now: number = Date.now()
): PrescribedServiceStatus => {
  const end = toTime(endDate);
  const deactivation = toTime(deactivationDate);

  if (deactivation !== null && (end === null || deactivation < end)) {
    return "inactive";
  }
  if (end !== null && endOfDay(end).getTime() < now) return "expired";
  return active === 1 ? "active" : "inactive";
};
